// compressImage.js — Client-side image compression using Canvas API (no npm dependency)

const THRESHOLD = 2 * 1024 * 1024; // 2MB

/**
 * Compress an image file if it exceeds the size threshold.
 * Pass 1: Resize to 1920px max, JPEG quality 0.7
 * Pass 2 (if still >2MB): Resize to 1280px, quality 0.5
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - The compressed (or original) file
 */
export const compressImage = (file) => {
  return new Promise((resolve) => {
    // Only compress images
    if (!file.type.startsWith('image/')) {
      return resolve(file);
    }

    // Skip if under threshold
    if (file.size <= THRESHOLD) {
      return resolve(file);
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Pass 1: 1920px max, quality 0.7
        const pass1 = resizeAndCompress(img, 1920, 0.7, file.name);
        if (pass1.size <= THRESHOLD) {
          return resolve(pass1);
        }

        // Pass 2: 1280px max, quality 0.5
        const pass2 = resizeAndCompress(img, 1280, 0.5, file.name);
        resolve(pass2);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

function resizeAndCompress(img, maxDimension, quality, originalName) {
  const canvas = document.createElement('canvas');
  let { width, height } = img;

  if (width > maxDimension || height > maxDimension) {
    if (width > height) {
      height = Math.round((height * maxDimension) / width);
      width = maxDimension;
    } else {
      width = Math.round((width * maxDimension) / height);
      height = maxDimension;
    }
  }

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  const blob = dataURLtoBlob(dataUrl);
  return new File([blob], originalName, { type: 'image/jpeg' });
}

function dataURLtoBlob(dataUrl) {
  const parts = dataUrl.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const bstr = atob(parts[1]);
  const u8arr = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new Blob([u8arr], { type: mime });
}

export default compressImage;
